"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { FileText } from "lucide-react";

const ReportCard = ({ image, title, pdfUrl, delay = 0 }) => {
    const hasValidImage = image &&
        (typeof image === "string" ? image.trim() !== "" : image?.src);
    const isValidPdf =
    typeof pdfUrl === "string" && pdfUrl.toLowerCase().endsWith(".pdf");

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.35, delay }}
            className="group relative overflow-hidden border border-gray-200/60 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-orange-200 hover:shadow-2xl hover:shadow-orange-100/30"
        >
            <div className="absolute inset-0 bg-gradient-to-br from-orange-50/0 to-amber-50/0 transition-all duration-300 group-hover:from-orange-50/60 group-hover:to-amber-50/40" />

            <div className="relative">
                {/* IMAGE WITH LINK */}
                <Link href={pdfUrl} target="_blank" rel="noopener noreferrer">
                    <div
                        className={`relative w-full cursor-pointer ${
                            hasValidImage
                                ? "h-[420px] bg-gray-100"
                                : "h-[200px] bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center"
                        }`}
                    >
                        {hasValidImage ? (
                            <>
                                <img
                                    src={typeof image === "string" ? image : image?.src}
                                    alt={title}
                                    className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                                />
                                <div className="absolute -top-1 -right-12 h-40 w-40 bg-gradient-to-br from-orange-400 to-amber-400 skew-x-12 opacity-0 transition-opacity duration-300 group-hover:opacity-20" />
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center text-center px-6 space-y-3">
                                <div className="w-20 h-20 bg-[#FFA201] rounded-2xl flex items-center justify-center shadow-lg border border-[#FFA201]">
                                    <FileText className="w-10 h-10 text-white" />
                                </div>
                            </div>
                        )}
                    </div>
                </Link>

                <div className="space-y-4 h-[150px] p-6">
                    {/* TITLE WITH LINK */}
                    <Link href={pdfUrl} target="_blank" rel="noopener noreferrer">
                        <h3
                            className="cursor-pointer text-md font-bold tracking-tight text-gray-900 transition-colors duration-300 group-hover:text-[#FFA201] mb-1"
                            style={{ fontFamily: "saans-bold" }}
                        >
                            {title}
                        </h3>
                    </Link>

                    {/* VIEW MORE BUTTON */}
                    <Link href={pdfUrl} target="_blank" rel="noopener noreferrer">
                        <motion.button
                            whileHover={{ x: 2 }}
                            whileTap={{ scale: 0.97 }}
                            className="relative inline-flex items-center gap-2 overflow-hidden border border-[#0475B3] bg-white px-4 py-2 text-sm font-semibold text-[#0475B3] shadow-sm transition-all duration-200 hover:border-[#0475B3] cursor-pointer focus:outline-none"
                            style={{ fontFamily: "saans-bold" }}
                        >
                            {/* <span className="absolute inset-0 z-0 translate-x-[-100%] bg-[#0475B3] transition-transform duration-200 group-hover:translate-x-0" /> */}

                            <span className="relative z-10 flex items-center gap-2">
                                {isValidPdf ? (<span>VIEW PDF</span>): (<span>VIEW More</span>)}
                                
                                <motion.svg
                                    whileHover={{ x: 3 }}
                                    className="h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2.2}
                                        d="M5 12h13M13 5l7 7-7 7"
                                    />
                                </motion.svg>
                            </span>
                        </motion.button>
                    </Link>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-1 origin-left scale-x-0 rounded-full bg-gradient-to-r from-[#FC9D00] to-[#FF8800] transition-transform duration-300 group-hover:scale-x-100" />
            </div>
        </motion.div>
    );
};

export default ReportCard;
